package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.ArchiveListingDto
import com.advaita.cloud.drive.shared.ArchiveListingJson
import com.advaita.cloud.drive.shared.ArchiveTree
import com.advaita.cloud.drive.shared.Delimiter
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.PreviewDescriptorDto
import com.advaita.cloud.drive.shared.PreviewOutcome
import com.advaita.cloud.drive.shared.PreviewRenderer
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.PreviewNotRenderableException
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.userFacingMessage
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

/** What the pane is currently able to draw. */
sealed interface PreviewContent {

    /** Fetching the descriptor or the bytes. */
    data object Loading : PreviewContent

    /** Decoded text for the TEXT renderer. */
    data class TextContent(val text: String, val truncated: Boolean) : PreviewContent

    /**
     * Delimited text for the TABULAR renderer (ADV-1202). Held as RAW TEXT, not
     * as a parsed grid: the delimiter and header controls re-parse on every
     * change, and at a 25 KB ceiling parsing is far cheaper than keeping two
     * representations in sync.
     */
    data class TableContent(val text: String, val truncated: Boolean) : PreviewContent

    /**
     * A downscaled image rendition (ADV-1207), held as a BLOB URL.
     *
     * A blob URL rather than the API path, because the bearer token lives in
     * JS: an `<img src="/api/...">` cannot authenticate, which is why every
     * renderer takes its bytes from this store. [objectUrl] is owned by the
     * store and revoked when the content is replaced — see [PreviewStore.setContent].
     *
     * [rendition] is the `X-Drive-Preview-Rendition` header, non-null only when
     * the bytes are NOT the file's own.
     */
    data class ImageContent(val objectUrl: String, val rendition: String?) : PreviewContent

    /**
     * An archive's entry listing (ADV-1228), with the folder tree built from it
     * ONCE, here, rather than on every recomposition.
     *
     * [truncated] is the `X-Drive-Preview-Truncated` header OR the payload's own
     * flag. They are written from one manifest and agree; taking either means a
     * disagreement can only ever show the notice, never hide it.
     */
    data class ArchiveContent(
        val listing: ArchiveListingDto,
        val tree: ArchiveTree,
        val truncated: Boolean,
    ) : PreviewContent

    /** No preview: unsupported type, over the ceiling, or not ready. */
    data class Unavailable(val message: String) : PreviewContent

    /** The fetch itself failed. Distinct from [Unavailable], which is a normal answer. */
    data class Failed(val message: String) : PreviewContent
}

/** Narrowest pane that still fits the header buttons and a readable line of text. */
private const val MIN_WIDTH_PX = 280

/** Leave at least this much for the file table, so the pane can never swallow the page. */
private const val MIN_TABLE_PX = 360

/**
 * The blob-URL pair, matching the download path's form in `FilesApi`.
 *
 * `URL` is reached through `js(...)` rather than the stdlib wrapper because
 * that is what the existing download does, and one spelling per codebase is
 * worth more than the typed call here.
 */
private fun createObjectUrl(blob: Blob): String = js("URL.createObjectURL")(blob) as String

private fun revokeObjectUrl(objectUrl: String) {
    js("URL.revokeObjectURL")(objectUrl)
}

/**
 * Re-wrap a response blob with the type the POLICY resolved.
 *
 * `/preview/content` answers `application/octet-stream` with `nosniff` on
 * purpose, so opening that URL directly can never execute anything. A blob URL
 * serves whatever type its blob declares, so the type is reattached here, at
 * the one point where a trusted server-side value is available.
 */
private fun retyped(blob: Blob, mimeType: String?): Blob =
    if (mimeType == null) blob else Blob(arrayOf(blob), BlobPropertyBag(type = mimeType))

private const val DEFAULT_WIDTH_PX = 420
private const val WIDTH_KEY = "drive.preview.width"

/**
 * State for the ADV-1200 viewer shell: whether the pane is up, which file it is
 * showing, what the server says can be drawn, and the pane's width.
 *
 * Deliberately separate from `FileBrowserStore`, which is already 810 lines and
 * owns listing, uploads, pulls, modals and toasts.
 *
 * This store owns the single authenticated fetch for preview bytes. The bearer
 * token lives in JS, so `<img src>` and `<iframe>` cannot reach `/api` at all;
 * every renderer therefore gets its content from here rather than fetching for
 * itself. Centralising that, plus the stale-response guard in [load], is the
 * point of the shell.
 *
 * [isOpen] and [selected] are separate on purpose. Once opened the pane stays
 * docked and closes only on the ✕, so moving to another folder blanks its
 * content instead of collapsing it — the table's width does not jump while you
 * browse. That makes "open with nothing selected" a real state.
 *
 * An earlier revision put that behaviour behind an explicit pin toggle. The
 * toggle was removed: it could not explain itself, and the behaviour is better
 * unconditional.
 */
class PreviewStore {

    /** Is the pane on screen? */
    var isOpen: Boolean by mutableStateOf(false)
        private set

    /** The file being previewed. Null while open is the empty state. */
    var selected: FileDto? by mutableStateOf(null)
        private set

    var descriptor: PreviewDescriptorDto? by mutableStateOf(null)
        private set

    var content: PreviewContent? by mutableStateOf(null)
        private set

    /**
     * Pane width in px. Re-clamped on load, NOT trusted verbatim: a width
     * persisted on a 2560px monitor would otherwise swallow a 1280px laptop
     * viewport and leave no table.
     */
    var widthPx: Int by mutableStateOf(
        clampWidth(localStorage.getItem(WIDTH_KEY)?.toIntOrNull() ?: DEFAULT_WIDTH_PX),
    )
        private set

    /**
     * ADV-1199 needs a set for multi-select and drag-to-move; widening this is
     * the intended path, which is why selection lives here rather than in the
     * table component.
     */
    val selectedFileId: String? get() = selected?.id

    /**
     * Delimiter chosen by the user, overriding detection. Reset per file, so a
     * choice made for one table never silently mis-parses the next one.
     */
    var delimiterOverride: Delimiter? by mutableStateOf(null)
        private set

    /**
     * Whether the first record is column names. A display preference, so it is
     * deliberately NOT reset per file — a user who turned it off means it.
     */
    var firstRowIsHeader: Boolean by mutableStateOf(true)
        private set

    fun overrideDelimiter(delimiter: Delimiter?) {
        delimiterOverride = delimiter
    }

    fun setFirstRowIsHeader(value: Boolean) {
        firstRowIsHeader = value
    }

    /**
     * The folder open inside an archive preview (ADV-1228), as path segments;
     * empty is the archive's root. Reset per file, like [delimiterOverride]:
     * a folder path of one archive means nothing in the next.
     */
    var archivePath: List<String> by mutableStateOf(emptyList())
        private set

    fun openArchiveFolder(path: List<String>) {
        archivePath = path
    }

    /**
     * In-flight load. Held so selecting another file abandons the previous
     * fetch — without this a slow first response can land after a later one and
     * repaint the pane with the wrong file's content.
     */
    private var loadJob: Job? = null

    /** Open the pane on [file]. Driven by a click on the file's row. */
    fun openFile(file: FileDto) {
        if (file.kind == FileKind.FOLDER) return
        // A FAILED pane must be re-clickable: after a transient 502 the only way
        // to retry used to be selecting a different file and coming back.
        if (isOpen && selected?.id == file.id && content !is PreviewContent.Failed) return
        clearLoad()
        isOpen = true
        delimiterOverride = null
        archivePath = emptyList()
        selected = file
        // The footer reads the descriptor. Left in place it reports the size
        // and the type of the PREVIOUS file under the new file's name, and a
        // failed descriptor request makes that mismatch permanent.
        descriptor = null
        setContent(PreviewContent.Loading)
        loadJob = MainScope().launch { load(file) }
    }

    /** Re-run the load for the current selection. Drives the Retry action. */
    fun retry() {
        val file = selected ?: return
        clearLoad()
        setContent(PreviewContent.Loading)
        loadJob = MainScope().launch { load(file) }
    }

    /** The ✕ — the only thing that closes the pane. */
    fun close() {
        clearLoad()
        isOpen = false
        selected = null
        descriptor = null
        setContent(null)
    }

    /**
     * Drop a selection whose row is gone — deleted, or we navigated to another
     * folder. The pane stays docked and goes empty rather than closing:
     * continuing to render a file that is not in view would be a lie, and
     * collapsing the pane would make the table jump on every folder change.
     */
    fun reconcile(files: List<FileDto>) {
        val current = selected ?: return
        if (files.any { it.id == current.id }) return
        clearLoad()
        selected = null
        descriptor = null
        setContent(null)
    }

    /** Live width during a drag. Not persisted per move — see [persistWidth]. */
    fun setWidth(px: Int) {
        widthPx = clampWidth(px)
    }

    /** Persist at drag END, so one drag is one write rather than hundreds. */
    fun persistWidth() {
        localStorage.setItem(WIDTH_KEY, widthPx.toString())
    }

    /**
     * Re-apply the clamp after the viewport changes.
     *
     * Without this, a width persisted on a large monitor survived onto a small
     * one: `clampWidth` ran only at construction and during a drag, and with
     * `body { overflow: hidden }` there is no page scroll to escape a squeezed
     * table. Wired to window `resize` in Main.kt.
     */
    fun reclampWidth() {
        widthPx = clampWidth(widthPx)
    }

    /** Keep the pane between its floor and whatever leaves the table usable. */
    fun clampWidth(px: Int): Int {
        val ceiling = maxOf(MIN_WIDTH_PX, window.innerWidth - MIN_TABLE_PX)
        return px.coerceIn(MIN_WIDTH_PX, ceiling)
    }

    private suspend fun load(file: FileDto) {
        try {
            val described = FilesApi.previewDescriptor(file.id)
            // A newer selection won this race while we were awaiting.
            if (selected?.id != file.id) return
            descriptor = described

            if (described.outcome != PreviewOutcome.RENDER) {
                setContent(PreviewContent.Unavailable(described.message ?: defaultUnavailable(described.outcome)))
                return
            }
            val fetched = FilesApi.previewContent(file.id)
            if (selected?.id != file.id) return

            val next = when (described.renderer) {
                PreviewRenderer.TEXT ->
                    // Excel and friends prepend a BOM; left in, it renders as a
                    // stray glyph at the top of the pane.
                    PreviewContent.TextContent(
                        fetched.response.text().await().removePrefix("\uFEFF"),
                        fetched.truncated,
                    )

                PreviewRenderer.TABULAR ->
                    PreviewContent.TableContent(fetched.response.text().await(), fetched.truncated)

                // ADV-1207. The body is a rendition, and the blob is re-typed
                // from the POLICY's mime type: the response itself is
                // deliberately octet-stream with nosniff, and a blob URL serves
                // whatever type its blob carries.
                PreviewRenderer.IMAGE -> {
                    val blob = fetched.response.blob().await()
                    // A newer selection may have won during that await. Check
                    // BEFORE minting a URL, or the URL leaks: setContent would
                    // never see it, so nothing would revoke it.
                    if (selected?.id != file.id) return
                    PreviewContent.ImageContent(
                        // The PAYLOAD's type when the server sent one: a TIFF
                        // preview is a PNG, and typing the blob image/tiff
                        // would draw a broken image.
                        objectUrl = createObjectUrl(
                            retyped(blob, fetched.payloadContentType ?: described.mimeType),
                        ),
                        rendition = fetched.rendition,
                    )
                }

                // Declared in the enum so this dispatch is exhaustive, but not
                // emitted by the backend policy yet. Each becomes a real branch
                // in its own subtask: ADV-1204, 1205, 1208, and svg/tif for
                // 1207. Reaching one today would mean the backend advertised a
                // renderer the client cannot draw, so say that rather than
                // showing an empty pane.
                // ADV-1228. The body is the listing as JSON, decoded by the same
                // codec the server encoded it with. A payload that does not
                // decode lands in the generic catch below as a failure.
                PreviewRenderer.ARCHIVE -> {
                    val listing = ArchiveListingJson.decode(fetched.response.text().await())
                    PreviewContent.ArchiveContent(
                        listing = listing,
                        tree = ArchiveTree.build(listing.entries),
                        truncated = fetched.truncated || listing.truncated,
                    )
                }

                PreviewRenderer.PDF,
                PreviewRenderer.RASTER,
                PreviewRenderer.UNSUPPORTED ->
                    PreviewContent.Unavailable("This preview is not available yet.")
            }
            setContent(next)
        } catch (e: kotlinx.coroutines.CancellationException) {
            // A newer selection cancelled us. Rethrow: swallowing it into the
            // generic catch below painted "StandaloneCoroutine was cancelled"
            // into the pane as though it were a fetch failure.
            throw e
        } catch (e: RedirectingException) {
            // A 401 already sent the page to /auth/login. Do not paint an error
            // over a navigation that is about to happen.
            throw e
        } catch (e: PreviewNotRenderableException) {
            // Policy refused the bytes after an optimistic descriptor. Take the
            // 409's descriptor as the truth — it carries the real outcome and
            // the footer reads the same field — and state the reason rather
            // than showing a red failure with a pointless Retry.
            if (selected?.id != file.id) return
            descriptor = e.descriptor
            setContent(
                PreviewContent.Unavailable(e.descriptor.message ?: defaultUnavailable(e.descriptor.outcome)),
            )
        } catch (e: Throwable) {
            if (selected?.id != file.id) return
            setContent(PreviewContent.Failed(e.userFacingMessage()))
        }
    }

    /**
     * The ONE place [content] is assigned, so a blob URL always gets revoked.
     *
     * An ImageContent holds a `blob:` URL, which pins its bytes in the browser
     * until it is revoked — a few hundred KB per image, leaked for the life of
     * the tab. Every path that replaces or clears the content comes through
     * here, which is why the field is written nowhere else.
     */
    private fun setContent(next: PreviewContent?) {
        val previous = content
        if (previous is PreviewContent.ImageContent && previous !== next) {
            revokeObjectUrl(previous.objectUrl)
        }
        content = next
    }

    private fun clearLoad() {
        loadJob?.cancel()
        loadJob = null
    }

    private fun defaultUnavailable(outcome: PreviewOutcome): String = when (outcome) {
        PreviewOutcome.TOO_LARGE -> "This file is too large to preview."
        PreviewOutcome.NOT_READY -> "This file is not ready to preview yet."
        PreviewOutcome.UNSUPPORTED_TYPE -> "There is no preview for this file type."
        PreviewOutcome.RENDER -> ""
    }
}
